import { PageContainer } from '../components/layout';
import { Spinner } from '../components/ui';
import { useRegisterMetrics } from '../hooks/useRegisterMetrics';
import TileGrid from '../components/tiles/TileGrid';
import styles from './DashboardHome.module.css';

/**
 * Dashboard home (04-05) — role-aware analytics tiles across all five registers.
 *
 * Replaces the 04-01 placeholder (App.tsx route already points here; this plan
 * only replaces the file contents — file-ownership §7). Renders a CMA-tone
 * greeting, then the `TileGrid` fed by `useRegisterMetrics` (role-scoped via
 * applyScope before aggregation). Shows a `Spinner` while loading and an honest
 * empty state against today's empty registers — the registers are EMPTY now and
 * populate as incidents are lodged / intake activates, so we say exactly that
 * rather than implying a fault.
 *
 * Urgency idiom (⚠️ + "N overdue" chips, SIRS chip, Risk rating strip) lives in
 * RegisterTile; purple stays identity-only (04-CONTEXT.md §5).
 *
 * 13-15: this page is now `/overview`, under "More". It is titled "Overview", and the
 * "Hello {first}" greeting is gone: the greeting lives on Today.
 */

export default function DashboardHome() {
  const { metrics, isLoading, error } = useRegisterMetrics();

  // Has anything to show across all five registers? (Drives the empty state.)
  const hasAnyRecords = Object.values(metrics).some((m) => m.total > 0);

  return (
    <PageContainer title="Overview">
      <p className={styles.intro}>
        Here’s your quality snapshot across incidents, complaints, improvements,
        feedback and risk — scoped to what your role manages.
      </p>

      {error && (
        <p role="alert" className={styles.error}>
          Couldn’t load some registers from the Quality &amp; Safety site:{' '}
          {error}
        </p>
      )}

      {isLoading ? (
        <div className={styles.loading} role="status" aria-live="polite">
          <Spinner size="lg" />
          <span className={styles.loadingText}>Loading your snapshot…</span>
        </div>
      ) : !hasAnyRecords && !error ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No items yet</p>
          <p className={styles.emptyBody}>
            The registers populate as incidents are lodged or intake activates.
            As soon as the first records land, you’ll see them here — with open
            counts, overdue nudges, and SIRS flags front and centre.
          </p>
        </div>
      ) : (
        <TileGrid metrics={metrics} />
      )}
    </PageContainer>
  );
}
